"use client";

import { analytics } from "@/lib/analytics/posthog-client";

/**
 * Get flag value from URL for debugging
 * Example: ?flags=hideBillingSection:true,hideUsersTab:true
 */
const getDebugFlag = (key: string, defaultValue: boolean): boolean => {
  if (typeof window === "undefined") {
    return defaultValue;
  }

  try {
    const url = new URL(window.location.href);
    const flagsParam = url.searchParams.get("flags");

    if (!flagsParam) {
      return defaultValue;
    }

    const flags = flagsParam.split(",").reduce(
      (acc, flag) => {
        const [flagKey, flagValue] = flag.split(":");
        acc[flagKey] = flagValue === "true";
        return acc;
      },
      {} as Record<string, boolean>
    );

    // Log debug flags if found
    if (Object.keys(flags).length > 0) {
      console.debug("[Feature Flags] Debug flags from URL:", flags);
    }

    // Return the flag value if it exists, otherwise default
    return flags[key] !== undefined ? flags[key] : defaultValue;
  } catch (e) {
    console.error("[Feature Flags] Error parsing debug flags:", e);
    return defaultValue;
  }
};

/**
 * Client-side feature flag implementation for the Pages Router
 * This uses PostHog's client-side feature flag detection
 *
 * Supports debug mode via URL parameters:
 * ?flags=flagName:true,anotherFlag:false
 */
export const useFeatureFlag = (key: string, defaultValue = false) => {
  // Simple cache living for the lifetime of the tab
  const fn = useFeatureFlag as any;
  if (!fn.__cache) {
    fn.__cache = new Map<string, boolean>();
  }
  const cache = fn.__cache as Map<string, boolean>;

  if (cache.has(key)) return cache.get(key)!;

  if (typeof window === "undefined") {
    return defaultValue;
  }

  // Check for debug flags in URL first
  const debugValue = getDebugFlag(key, defaultValue);
  if (debugValue !== defaultValue) {
    console.debug(
      `[Feature Flag] 🔧 OVERRIDE: ${key} = ${debugValue} (from URL parameter)`
    );
    // Cache the debug value
    cache.set(key, debugValue);
    return debugValue;
  }

  // Check if analytics is initialized
  if (!analytics || !analytics.__loaded) {
    console.debug(
      `[Feature Flag] PostHog not loaded, using default for ${key}: ${defaultValue}`
    );
    // Don't cache this result to allow retrying once analytics loads
    return defaultValue;
  }

  // Use PostHog's isFeatureEnabled method
  const flagValue =
    analytics.isFeatureEnabled(key, { send_event: true }) ?? defaultValue;
  console.debug(`[Feature Flag] ${key} = ${flagValue}`);

  // Cache the result to avoid repeated calls and events
  cache.set(key, flagValue);
  return flagValue;
};
